import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Wind, Flower2, Soup, Footprints } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";

const Section = ({ num, tag, title, children, flip = false }) => (
  <section className="mx-auto max-w-6xl px-6 pt-32">
    <div className={`grid items-start gap-14 lg:grid-cols-2 ${flip ? "" : ""}`}>
      {children}
    </div>
  </section>
);

const TreatCard = ({ title, text, to }) => (
  <Link
    to={to}
    data-testid={`treat-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="group block rounded-[2rem] bg-cream-soft p-8 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_48px_rgba(48,51,48,0.09)]"
  >
    <h4 className="font-serif text-xl italic">{title}</h4>
    <p className="mt-3 text-sm leading-relaxed text-ink/60">{text}</p>
    <span className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sage">
      Prenota
      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1.5" />
    </span>
  </Link>
);

const PERCORSI = [
  { title: "Radicamento e Leggerezza", text: "Per ansia e bisogno di controllo: tornare al corpo, un respiro alla volta." },
  { title: "Bacino, Respiro e Pavimento Pelvico", text: "Riconnettersi con la base, custode di forza e creatività." },
  { title: "Sciogliere il Trattenuto", text: "Per spalle, collo e diaframma tesi: liberare ciò che pesa." },
  { title: "Drenare e Riattivare", text: "Per gonfiore e ristagno: rimettere in flusso l'energia vitale." },
  { title: "Femminile, Ciclicità e Ascolto", text: "Comprendere e onorare il ritmo naturale del corpo femminile." },
];

const Servizi = () => (
  <div data-testid="servizi-page" className="pt-40">
    <header className="mx-auto max-w-6xl px-6">
      <Reveal y={16}><Eyebrow>Servizi</Eyebrow></Reveal>
      <h1 className="mt-6 font-serif text-5xl leading-tight sm:text-6xl" data-testid="servizi-title">
        <MaskedLine delay={0.1} inView>Servizi &amp;</MaskedLine>
        <MaskedLine delay={0.22} inView><span className="italic text-sage">Trattamenti</span></MaskedLine>
      </h1>
      <Reveal delay={0.35}>
        <p className="mt-7 max-w-xl text-base leading-relaxed text-ink/65">
          Un invito alla lentezza. Ogni trattamento è un rituale sartoriale disegnato per
          riportare il corpo al suo ritmo naturale, dove il tocco si fa ascolto e la
          presenza diventa guarigione.
        </p>
      </Reveal>
    </header>

    {/* 01 CORPOREO */}
    <Section>
      <div>
        <Reveal><p className="text-xs font-medium uppercase tracking-[0.35em] text-earth">01 / Radici e Flusso</p></Reveal>
        <Reveal delay={0.08}><h2 className="mt-5 font-serif text-4xl" data-testid="corporeo-title">Benessere Corporeo</h2></Reveal>
        <Reveal delay={0.14}><p className="mt-5 max-w-md text-sm leading-relaxed text-ink/60">Per tensioni fisiche e bisogno di recupero: il corpo ritrova morbidezza e spazio.</p></Reveal>
        <div className="mt-9 grid gap-5 sm:grid-cols-2">
          <Reveal delay={0.1}><TreatCard title="Massaggio Olistico" text="Un trattamento su misura che scioglie le tensioni fisiche e riequilibra le energie, tra olio caldo e ascolto profondo." to="/contatti?servizio=Massaggio%20Olistico" /></Reveal>
          <Reveal delay={0.18}><TreatCard title="5 Elementi" text="Sincronizzare il corpo con i ritmi di Terra, Acqua, Fuoco, Aria ed Etere per una vitalità profonda." to="/contatti?servizio=Trattamento%205%20Elementi" /></Reveal>
          <Reveal delay={0.26}><TreatCard title="Riflessologia Plantare" text="Il corpo intero raccontato dai piedi: punti riflessi per riequilibrare organi ed emozioni." to="/contatti?servizio=Riflessologia%20Plantare" /></Reveal>
          <Reveal delay={0.34}><TreatCard title="Massaggio Decontratturante" text="Un lavoro profondo e mirato per liberare schiena, spalle e collo dalle contratture." to="/contatti?servizio=Massaggio%20Decontratturante" /></Reveal>
        </div>
      </div>
      <Reveal delay={0.2} className="lg:pl-10">
        <div className="overflow-hidden rounded-[3rem]">
          <img src="/assets/massaggio-2.jpg" alt="Trattamento di massaggio" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
        </div>
      </Reveal>
    </Section>

    {/* 02 MEDITATIVO */}
    <Section>
      <Reveal className="order-2 lg:order-1 lg:pr-10">
        <div className="overflow-hidden rounded-[3rem]">
          <img src="/assets/agnese-yoga.jpg" alt="Pratica di yoga dolce" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
        </div>
      </Reveal>
      <div className="order-1 lg:order-2">
        <Reveal><p className="text-xs font-medium uppercase tracking-[0.35em] text-lavanda">02 / Silenzio e Respiro</p></Reveal>
        <Reveal delay={0.08}><h2 className="mt-5 font-serif text-4xl" data-testid="meditativo-title">Benessere Meditativo</h2></Reveal>
        <Reveal delay={0.14}><p className="mt-5 max-w-md text-sm leading-relaxed text-ink/60">Per chi fatica a lasciare andare la mente: pratiche gentili che riportano al presente.</p></Reveal>
        <div className="mt-9 space-y-5">
          {[
            { icon: Wind, title: "Il Respiro Consapevole", text: "L'arte di abitare il soffio per calmare la mente e ossigenare l'anima.", bg: "bg-lavanda-mist text-lavanda", q: "Respiro%20Consapevole" },
            { icon: Flower2, title: "Yoga Dolce", text: "Movimenti fluidi e asana meditative per riscoprire la propria flessibilità interiore.", bg: "bg-sage-mist text-sage", q: "Yoga%20Dolce" },
            { icon: Sparkles, title: "Meditazione Guidata", text: "Visualizzazioni e silenzio condiviso per coltivare una quiete che resta.", bg: "bg-earth-mist text-earth", q: "Meditazione%20Guidata" },
          ].map((s, i) => (
            <Reveal key={s.title} delay={0.1 * i}>
              <Link to={`/contatti?servizio=${s.q}`} data-testid={`med-${i}`} className="group flex items-center gap-6 rounded-[2rem] bg-cream-soft p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(48,51,48,0.08)]">
                <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full ${s.bg}`}>
                  <s.icon className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-serif text-lg">{s.title}</h4>
                  <p className="mt-1 text-sm text-ink/60">{s.text}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>

    {/* 03 FEMMINILE + 04 DIGESTIVO */}
    <Section>
      <Reveal>
        <div className="h-full rounded-[3rem] bg-earth-mist p-10 sm:p-12">
          <p className="text-xs font-medium uppercase tracking-[0.35em] text-earth">03 / Focus Sacro</p>
          <h2 className="mt-5 font-serif text-4xl" data-testid="femminile-title">Benessere Femminile</h2>
          <p className="mt-5 text-sm leading-relaxed text-ink/65">
            Un percorso dedicato alla riscoperta del centro vitale. Pavimento pelvico e
            bacino come custodi di forza e creatività.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            {["Pavimento Pelvico", "Ciclicità", "Bacino"].map((c) => (
              <span key={c} className="rounded-full bg-cream-soft px-5 py-2 text-xs font-medium text-earth">{c}</span>
            ))}
          </div>
          <div className="mt-9 overflow-hidden rounded-[2rem]">
            <img src="/assets/femminile-loto.jpg" alt="Il bacino come fiore di loto" className="aspect-video w-full object-cover" />
          </div>
          <Link to="/contatti?servizio=Benessere%20Femminile" data-testid="femminile-cta" className="mt-8 inline-block rounded-full bg-sage px-7 py-3.5 text-sm font-medium text-cream transition-transform duration-500 hover:scale-105">
            Scopri il servizio
          </Link>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="h-full rounded-[3rem] bg-sage-soft/70 p-10 sm:p-12">
          <p className="text-xs font-medium uppercase tracking-[0.35em] text-sage-deep">04 / Centro e Radici</p>
          <h2 className="mt-5 font-serif text-4xl" data-testid="digestivo-title">Benessere Digestivo</h2>
          <p className="mt-5 text-sm leading-relaxed text-ink/65">
            Per stress e somatizzazioni: libera l'addome e il diaframma dalle emozioni
            trattenute. Una leggerezza che parte dal centro.
          </p>
          <ul className="mt-7 space-y-3 text-sm text-ink/65">
            <li className="flex items-center gap-3"><Footprints className="h-4 w-4 text-sage" /> Lavoro su diaframma e addome</li>
            <li className="flex items-center gap-3"><Wind className="h-4 w-4 text-sage" /> Respirazione diaframmatica</li>
            <li className="flex items-center gap-3"><Sparkles className="h-4 w-4 text-sage" /> Riequilibrio emozionale</li>
          </ul>
          <Link to="/contatti?servizio=Benessere%20Digestivo" data-testid="digestivo-cta" className="mt-9 inline-block rounded-full bg-sage px-7 py-3.5 text-sm font-medium text-cream transition-transform duration-500 hover:scale-105">
            Prenota una sessione
          </Link>
        </div>
      </Reveal>
    </Section>

    {/* 05 CUCINA */}
    <Section>
      <div>
        <Reveal><p className="text-xs font-medium uppercase tracking-[0.35em] text-lavanda">05 / Tavola Consapevole</p></Reveal>
        <Reveal delay={0.08}><h2 className="mt-5 font-serif text-4xl" data-testid="cucina-title">Cucina Consapevole &amp; Mindful Eating</h2></Reveal>
        <Reveal delay={0.14}>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-ink/60">
            Il cibo come cura e meditazione. Laboratori di cucina ayurvedica e pasti in
            consapevolezza per trasformare il rapporto con ciò che nutre.
          </p>
        </Reveal>
        <div className="mt-9 grid gap-5 sm:grid-cols-2">
          <Reveal delay={0.1}><TreatCard title="Laboratori di Cucina" text="Cuciniamo insieme piatti di stagione con i cinque sapori ayurvedici, tra gesti lenti e cura." to="/contatti?servizio=Laboratorio%20Cucina%20Consapevole" /></Reveal>
          <Reveal delay={0.18}><TreatCard title="Mindful Eating" text="Il pasto come meditazione: mangiare con tutti i sensi, in presenza e gratitudine." to="/contatti?servizio=Mindful%20Eating" /></Reveal>
        </div>
      </div>
      <Reveal delay={0.2} className="lg:pl-10">
        <div className="overflow-hidden rounded-[3rem]">
          <img src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?q=80&w=1200&auto=format&fit=crop" alt="Cucina consapevole" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
        </div>
      </Reveal>
    </Section>

    {/* PERCORSI */}
    <section className="mx-auto max-w-6xl px-6 pt-36">
      <Reveal className="text-center">
        <Eyebrow className="text-center">Viaggi Trasformativi</Eyebrow>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl" data-testid="percorsi-title">I Percorsi Artemisia</h2>
        <p className="mx-auto mt-5 max-w-lg text-base text-ink/60">
          Viaggi di più incontri per una rinascita completa, costruiti su di te.
        </p>
      </Reveal>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {PERCORSI.map((p, i) => (
          <Reveal key={p.title} delay={0.07 * i} className={i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}>
            <Link
              to={`/contatti?servizio=Percorso%20${encodeURIComponent(p.title)}`}
              data-testid={`percorso-card-${i}`}
              className="group flex h-full flex-col rounded-[2.5rem] bg-cream-soft p-9 transition-all duration-500 hover:-translate-y-2 hover:bg-sage-mist hover:shadow-[0_24px_56px_rgba(48,51,48,0.1)]"
            >
              <span className="font-serif text-4xl italic text-sage/30 transition-colors duration-500 group-hover:text-sage">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-5 font-serif text-xl">{p.title}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/60">{p.text}</p>
              <Soup className="mt-6 h-5 w-5 text-lavanda/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            </Link>
          </Reveal>
        ))}
        <Reveal delay={0.4}>
          <div className="flex h-full flex-col items-start justify-center rounded-[2.5rem] bg-sage-deep p-9 text-cream">
            <h3 className="font-serif text-2xl italic">Non sai da dove iniziare?</h3>
            <p className="mt-3 text-sm text-cream/70">Scrivimi: troveremo insieme il percorso giusto per te.</p>
            <Link to="/contatti" data-testid="percorsi-cta" className="mt-7 rounded-full bg-cream px-7 py-3.5 text-sm font-semibold text-sage-deep transition-transform duration-500 hover:scale-105">
              Parliamone
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Servizi;
